import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plane, Hotel, ArrowLeft, Check, AlertTriangle,
  Loader2, RefreshCw, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import {
  FlightBookingConfirmation,
  HotelBookingConfirmation,
  CancellationConfirmation
} from '../components/booking/BookingConfirmation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Booking flow states
const FLOW_STATES = {
  LOADING: 'loading',
  SELECTION: 'selection',
  REVIEW: 'review',
  CONFIRMATION: 'confirmation',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  ERROR: 'error',
  VERIFYING: 'verifying'
};

const SafeBookingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [flowState, setFlowState] = useState(FLOW_STATES.LOADING);
  const [bookingType, setBookingType] = useState(null); // 'flight' or 'hotel'
  const [selectionData, setSelectionData] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  const [error, setError] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);

  // Initialize from location state
  useEffect(() => {
    const initBooking = async () => {
      const state = location.state;
      
      if (!state) {
        toast.error('No booking data found');
        navigate('/travel');
        return;
      }

      setBookingType(state.type);
      setSelectionData(state.selection);
      
      if (state.reviewData) {
        setReviewData(state.reviewData);
      }
      
      if (state.pricingData) {
        setPricingData(state.pricingData);
      }

      // If we have review data, go to confirmation
      if (state.reviewData) {
        setFlowState(FLOW_STATES.CONFIRMATION);
      } else {
        // Need to get review first
        setFlowState(FLOW_STATES.REVIEW);
        await performReview(state.type, state.selection);
      }
    };

    initBooking();
  }, [location.state, navigate]);

  // Perform review/validation step
  const performReview = async (type, selection) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      if (type === 'flight') {
        const response = await fetch(`${API_URL}/api/safe-booking/flight/pre-validate`, {
          method: 'POST',
          headers,
          body: JSON.stringify(selection.priceIds || [selection.priceId])
        });
        const data = await response.json();
        
        if (data.success) {
          setReviewData(data);
          setFlowState(FLOW_STATES.CONFIRMATION);
        } else {
          setError(data.error || 'Fare validation failed');
          setFlowState(FLOW_STATES.ERROR);
        }
      } else if (type === 'hotel') {
        const response = await fetch(
          `${API_URL}/api/safe-booking/hotel/pre-validate?correlation_id=${selection.correlationId}&hotel_id=${selection.hotelId}&option_id=${selection.optionId}&review_hash=${encodeURIComponent(selection.reviewHash)}`,
          { method: 'POST', headers }
        );
        const data = await response.json();
        
        if (data.success) {
          setReviewData(data);
          setFlowState(FLOW_STATES.CONFIRMATION);
        } else {
          setError(data.error || 'Hotel validation failed');
          setFlowState(FLOW_STATES.ERROR);
        }
      }
    } catch (err) {
      console.error('Review error:', err);
      setError('Failed to validate booking. Please try again.');
      setFlowState(FLOW_STATES.ERROR);
    }
  };

  // Handle flight booking confirmation
  const handleFlightConfirm = async (confirmData) => {
    setFlowState(FLOW_STATES.PROCESSING);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/safe-booking/flight/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          bookingId: confirmData.bookingId,
          idempotencyKey: confirmData.idempotencyKey,
          passengers: selectionData.passengers || [{
            title: 'Mr',
            firstName: 'Guest',
            lastName: 'User',
            passengerType: 'ADULT'
          }],
          contactEmail: selectionData.contactEmail || 'guest@example.com',
          contactPhone: selectionData.contactPhone || '+911234567890',
          userConfirmed: true
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setBookingResult(data);
        setFlowState(FLOW_STATES.VERIFYING);
        
        // Start verification
        await verifyBooking('flight', data.booking_ref);
      } else {
        throw new Error(data.detail || data.error || 'Booking failed');
      }
    } catch (err) {
      console.error('Booking error:', err);
      setError(err.message);
      setFlowState(FLOW_STATES.ERROR);
      throw err;
    }
  };

  // Handle hotel booking confirmation
  const handleHotelConfirm = async (confirmData) => {
    setFlowState(FLOW_STATES.PROCESSING);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/safe-booking/hotel/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          bookingId: confirmData.bookingId,
          idempotencyKey: confirmData.idempotencyKey,
          amount: confirmData.amount,
          guests: selectionData.guests || [{
            title: 'Mr',
            firstName: 'Guest',
            lastName: 'User'
          }],
          contactEmail: selectionData.contactEmail || 'guest@example.com',
          contactPhone: selectionData.contactPhone || '+911234567890',
          instantBook: true,
          userConfirmed: true
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setBookingResult(data);
        setFlowState(FLOW_STATES.VERIFYING);
        
        // For hotel, verification note applies
        toast.info('Verifying booking status...');
        await verifyBooking('hotel', data.booking_ref);
      } else {
        throw new Error(data.detail || data.error || 'Booking failed');
      }
    } catch (err) {
      console.error('Booking error:', err);
      setError(err.message);
      setFlowState(FLOW_STATES.ERROR);
      throw err;
    }
  };

  // Verify booking status
  const verifyBooking = async (type, bookingRef) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/safe-booking/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          bookingId: bookingRef,
          bookingType: type
        })
      });

      const data = await response.json();
      setVerificationStatus(data);
      
      if (data.success) {
        setFlowState(FLOW_STATES.SUCCESS);
        toast.success('Booking confirmed!');
      } else if (data.requires_manual_check) {
        // Verification inconclusive - show success with note
        setFlowState(FLOW_STATES.SUCCESS);
        toast.warning('Booking initiated. Please check your email for confirmation.');
      } else {
        setFlowState(FLOW_STATES.SUCCESS);
      }
    } catch (err) {
      console.error('Verification error:', err);
      // Still show success - booking was made, verification is secondary
      setFlowState(FLOW_STATES.SUCCESS);
      toast.warning('Booking initiated. Verification pending.');
    }
  };

  // Render based on flow state
  const renderContent = () => {
    switch (flowState) {
      case FLOW_STATES.LOADING:
      case FLOW_STATES.REVIEW:
        return (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mb-4" />
            <p className="text-gray-400">Validating your selection...</p>
          </div>
        );

      case FLOW_STATES.CONFIRMATION:
        if (bookingType === 'flight') {
          return (
            <FlightBookingConfirmation
              flightData={selectionData}
              reviewData={reviewData}
              onConfirm={handleFlightConfirm}
              onCancel={() => navigate(-1)}
              onBack={() => navigate(-1)}
            />
          );
        } else {
          return (
            <HotelBookingConfirmation
              hotelData={selectionData}
              reviewData={reviewData}
              pricingData={pricingData}
              onConfirm={handleHotelConfirm}
              onCancel={() => navigate(-1)}
              onBack={() => navigate(-1)}
            />
          );
        }

      case FLOW_STATES.PROCESSING:
        return (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <div className="relative">
              <Loader2 className="w-16 h-16 animate-spin text-indigo-500" />
              <div className="absolute inset-0 flex items-center justify-center">
                {bookingType === 'flight' ? (
                  <Plane className="w-6 h-6 text-white" />
                ) : (
                  <Hotel className="w-6 h-6 text-white" />
                )}
              </div>
            </div>
            <p className="text-white text-lg mt-6">Processing your booking...</p>
            <p className="text-gray-400 text-sm mt-2">Please do not close this page</p>
          </div>
        );

      case FLOW_STATES.VERIFYING:
        return (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <RefreshCw className="w-12 h-12 animate-spin text-blue-500 mb-4" />
            <p className="text-white text-lg">Verifying booking status...</p>
            <p className="text-gray-400 text-sm mt-2">This may take a moment</p>
          </div>
        );

      case FLOW_STATES.SUCCESS:
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--bg-secondary)] rounded-2xl border border-white/10 p-8 text-center"
          >
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-400" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">
              Booking Initiated!
            </h2>
            
            <p className="text-gray-400 mb-6">
              Your {bookingType} booking has been processed successfully.
            </p>

            {bookingResult && (
              <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
                <p className="text-gray-400 text-sm">Booking Reference</p>
                <p className="text-white font-mono text-lg">{bookingResult.booking_ref}</p>
                
                {verificationStatus && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <p className="text-gray-400 text-sm">Status</p>
                    <p className="text-green-400 font-medium">
                      {verificationStatus.status || 'Confirmed'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {verificationStatus?.requires_manual_check && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-2 text-amber-400">
                  <Clock className="w-5 h-5" />
                  <span className="font-medium">Verification Pending</span>
                </div>
                <p className="text-amber-200/70 text-sm mt-2">
                  Please check your email for booking confirmation. 
                  You can also check the booking status in your account.
                </p>
              </div>
            )}

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/travel')}
                className="px-6 py-3 rounded-xl border border-white/20 text-white hover:bg-white/5"
              >
                Back to Travel
              </button>
              <button
                onClick={() => navigate('/my-bookings')}
                className="px-6 py-3 rounded-xl bg-indigo-600 text-white hover:bg-indigo-500"
              >
                View My Bookings
              </button>
            </div>
          </motion.div>
        );

      case FLOW_STATES.ERROR:
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--bg-secondary)] rounded-2xl border border-white/10 p-8 text-center"
          >
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">
              Booking Failed
            </h2>
            
            <p className="text-gray-400 mb-4">
              {error || 'Something went wrong with your booking.'}
            </p>

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-3 rounded-xl border border-white/20 text-white hover:bg-white/5"
              >
                Go Back
              </button>
              <button
                onClick={() => {
                  setError(null);
                  setFlowState(FLOW_STATES.REVIEW);
                  performReview(bookingType, selectionData);
                }}
                className="px-6 py-3 rounded-xl bg-indigo-600 text-white hover:bg-indigo-500"
              >
                Try Again
              </button>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        {flowState !== FLOW_STATES.SUCCESS && flowState !== FLOW_STATES.ERROR && (
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
        )}

        {/* Progress indicator */}
        {flowState !== FLOW_STATES.SUCCESS && flowState !== FLOW_STATES.ERROR && (
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className={`w-3 h-3 rounded-full ${flowState === FLOW_STATES.REVIEW ? 'bg-indigo-500' : 'bg-white/20'}`} />
            <div className={`w-12 h-0.5 ${flowState === FLOW_STATES.CONFIRMATION ? 'bg-indigo-500' : 'bg-white/20'}`} />
            <div className={`w-3 h-3 rounded-full ${flowState === FLOW_STATES.CONFIRMATION ? 'bg-indigo-500' : 'bg-white/20'}`} />
            <div className={`w-12 h-0.5 ${flowState === FLOW_STATES.PROCESSING ? 'bg-indigo-500' : 'bg-white/20'}`} />
            <div className={`w-3 h-3 rounded-full ${flowState === FLOW_STATES.PROCESSING || flowState === FLOW_STATES.VERIFYING ? 'bg-indigo-500' : 'bg-white/20'}`} />
          </div>
        )}

        {/* Main content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={flowState}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SafeBookingPage;
